import {Component, Input, OnInit} from '@angular/core';
import * as d3 from "d3";
import * as tubeMap from "../../assets/js/d3-tube-map/src";
import {Shipment} from "../services/shipment";
import {ShipmentsService} from "../services/shipments.service";

@Component({
    selector: 'app-shipment-diagram',
    templateUrl: './shipment-diagram.component.html',
    styleUrls: ['./shipment-diagram.component.scss']
})
export class ShipmentDiagramComponent implements OnInit {


    protected column = 17;
    protected backgroundLineColor = '#cccccc';
    protected estimatedLineColor = '#f9d348';
    protected progressLineColor = '#3a8cbc';

    @Input() shipment: Shipment;
    @Input() dev = true; // @todo set false in prod

    debugging = false;
    testing;
    iteration = 0;
    rejections = 0;
    error;

    carrierName = 'Fragtudbyder';

    constructor(public shipmentsService: ShipmentsService) {
    }

    ngOnInit(): void {
        switch (this.shipment.carrier_code) {
            case '9':
                this.carrierName = 'PostNord';
                break;

            case '11':
                this.carrierName = 'GLS';
                break;

            case '10':
                this.carrierName = 'Danske Fragtmænd';
                break;
        }

        this.draw();
    }

    public draw() {
        let map = tubeMap.tubeMap()
            .width(500)
            .height(500)
            .margin({
                top: 5,
                right: 0,
                bottom: 0,
                left: 5,
            })
            .on("click", function (name) {
                alert('Der blev trykket på ' + name); // @todo
            });

        d3.select('#tube-map').html(null).datum(this.lines()).call(map);
    }

    protected lines() {
        let lines = this.backgroundLines();

        lines.push({
            name: "Estimate",
            color: this.estimatedLineColor,
            dashed: false,
            shiftCoords: [0, 0],
            nodes: this.lineNodes(this.shipment.estimated_delivery_progress)
        });

        lines.push({
            name: "Progress",
            color: this.progressLineColor,
            dashed: false,
            shiftCoords: [0, 0],
            nodes: this.lineNodes(this.shipment.delivery_progress)
        });

        return {
            stations: {
                WebOrdre: {
                    label: this.getLabel(0, "Web ordre"),
                },
                IdaOrdre: {
                    label: this.getLabel(1, "IDA ordre")
                },
                IkkePaaLager: {
                    label: this.getLabel(2, "Ikke på lager")
                },
                Lagervare: {
                    label: this.getLabel(3, "Lagervare")
                },
                BestiltLeverandoer: {
                    label: this.getLabel(4, "Bestilt leverandør")
                },
                BestiltFjernLager: {
                    label: this.getLabel(5, "Bestilt Fjernlager")
                },
                DropShipment: {
                    label: this.getLabel(6, "Drop shipment")
                },
                PlukningStartet: {
                    label: this.getLabel(7, "Plukning startet")
                },
                Pakket: {
                    label: this.getLabel(9, "Pakket")
                },
                Ankommet: {
                    label: this.getLabel(8, "Ankommet")
                },
                KlarTilAfhentning: {
                    label: this.getLabel(10, "Klar til afhentning")
                },
                TildeltChauffoer: {
                    label: this.getLabel(11, "Tildelt chauffør")
                },
                Annulleret: {
                    label: this.getLabel(16, "Annulleret")
                },
                RutePaabegyndt: {
                    label: this.getLabel(13, "Rute påbegyndt")
                },
                Skadet: {
                    label: this.getLabel(15, "Skadet")
                },
                ModtagetAfKunde: {
                    label: this.getLabel(14, "Modtaget af kunde")
                },
                HentetAfFragtudbyder: {
                    label: this.getLabel(12, 'Hentet af ' + this.carrierName)
                },
                Boxen: {
                    label: this.getLabel(17, '10-4 Boxen')
                },
                BoxenRumBooket: {
                    label: this.getLabel(18, 'Rum booket')
                },
                BoxenSmsSendt: {
                    label: this.getLabel(19, 'SMS sendt')
                }
            },
            lines: lines
        };
    }

    protected backgroundLines() {
        /**
         * Blue
         */
        let nodesBlue = [].concat(this.webOrdreNodes());
        nodesBlue = nodesBlue.concat(this.webOrdreAfterNodes());
        nodesBlue = nodesBlue.concat(this.ikkePaaLagerNodes());
        nodesBlue = nodesBlue.concat(this.bestiltFjernLagerNodes());
        nodesBlue = nodesBlue.concat(this.ankommetNodes());
        nodesBlue = nodesBlue.concat(this.ankommetAfterNodes());
        nodesBlue = nodesBlue.concat([{coords: [this.column * 4, -20]}]);

        /**
         *  Green
         */
        let nodesGreen = [{coords: [this.column, -20]}];
        nodesGreen = nodesGreen.concat(this.bestiltLeverandoerNodes());
        nodesGreen = nodesGreen.concat(this.bestiltLeverandoerAfterNodes());
        nodesGreen = nodesGreen.concat([{coords: [this.column, -30]}]);

        /**
         * Red
         */
        let nodesRed = [].concat(this.idaOrdreNodes());
        nodesRed = nodesRed.concat(this.idaOrdreAfterNodes());
        nodesRed = nodesRed.concat(this.lagervareNodes());
        nodesRed = nodesRed.concat(this.plukningStartetNodes());
        nodesRed = nodesRed.concat(this.pakketNodes());
        nodesRed = nodesRed.concat(this.hentetAfFragtudbyderNodes());
        nodesRed = nodesRed.concat(this.hentetAfFragtudbyderAfterNodes());
        nodesRed = nodesRed.concat(this.annulleretBeforeNodes());
        nodesRed = nodesRed.concat(this.annulleretNodes());

        /**
         * Yellow
         */
        let nodesYellow = [{coords: [this.column * 4, -10]}];
        nodesYellow = nodesYellow.concat(this.dropShipmentNodes());

        /**
         * Orange
         */
        let nodesOrange = [{coords: [this.column * 4, -45]}];
        nodesOrange = nodesOrange.concat(this.klarTilAfhentningNodes());
        nodesOrange = nodesOrange.concat(this.klarTilAfhentningAfterNodes());
        nodesOrange = nodesOrange.concat(this.modtagetAfKundeNodes());

        /**
         * Purple
         */
        let nodesPurple = [{coords: [this.column * 3, -45]}];
        nodesPurple = nodesPurple.concat(this.tildeltChauffoerNodes());
        nodesPurple = nodesPurple.concat(this.rutePaabegyndtNodes());
        nodesPurple = nodesPurple.concat(this.skadetNodes());
        nodesPurple = nodesPurple.concat(this.skadetAfterNodes());
        nodesPurple = nodesPurple.concat([{coords: [this.column * 4, -75]}]);

        /**
         * Turquoise
         */
        let nodesTurquoise = [{coords: [this.column * 4, -45]}];
        nodesTurquoise = nodesTurquoise.concat(this.boxenNodes());
        nodesTurquoise = nodesTurquoise.concat(this.boxenRumBooketNodes());
        nodesTurquoise = nodesTurquoise.concat(this.boxenSmsSendtNodes());
        nodesTurquoise = nodesTurquoise.concat(this.boxenSmsSendtAfterNodes());

        /**
         * Magenta
         */
        let nodesMagenta = [{coords: [this.column, -20]}];
        nodesMagenta = nodesMagenta.concat([{coords: [this.column * 2 - 1, -20]}]);
        nodesMagenta = nodesMagenta.concat([{coords: [this.column * 2, -21]}]);
        nodesMagenta = nodesMagenta.concat([{coords: [this.column * 2, -29]}]);
        nodesMagenta = nodesMagenta.concat([{coords: [this.column * 2 - 1, -30]}]);
        nodesMagenta = nodesMagenta.concat([{coords: [this.column, -30]}]);

        /**
         * Prepare lines for map
         */
        return [
            {
                name: "Blue",
                color: !this.debugging ? this.backgroundLineColor : "#0066ff",
                dashed: true,
                shiftCoords: [0, 0],
                nodes: nodesBlue
            },
            {
                name: "Green",
                color: !this.debugging ? this.backgroundLineColor : "#00ff00",
                dashed: true,
                shiftCoords: [0, 0],
                nodes: nodesGreen
            },
            {
                name: "Red",
                color: !this.debugging ? this.backgroundLineColor : "#ff0000",
                dashed: true,
                shiftCoords: [0, 0],
                nodes: nodesRed
            },
            {
                name: "Yellow",
                color: !this.debugging ? this.backgroundLineColor : "#ffed00",
                dashed: true,
                shiftCoords: [0, 0],
                nodes: nodesYellow
            },
            {
                name: "Orange",
                color: !this.debugging ? this.backgroundLineColor : "#ff9900",
                dashed: true,
                shiftCoords: [0, 0],
                nodes: nodesOrange
            },
            {
                name: "Purple",
                color: !this.debugging ? this.backgroundLineColor : "#7b00ff",
                dashed: true,
                shiftCoords: [0, 0],
                nodes: nodesPurple
            },
            {
                name: "Turquoise",
                color: !this.debugging ? this.backgroundLineColor : "#30D5C8",
                dashed: true,
                shiftCoords: [0, 0],
                nodes: nodesTurquoise
            },
            {
                name: "Magenta",
                color: !this.debugging ? this.backgroundLineColor : "#e116d6",
                dashed: true,
                shiftCoords: [0, 0],
                nodes: nodesMagenta
            },
        ];
    }

    protected lineNodes(bits: number) {
        let nodes = [];

        // Web/Ida
        if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.webOrdre)) {
            nodes = nodes.concat(this.webOrdreNodes());

            if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.ikkePaaLager) || this.getBit(bits, this.shipmentsService.deliveryBitMasks.lagervare) || this.getBit(bits, this.shipmentsService.deliveryBitMasks.dropShipment)) {
                nodes = nodes.concat(this.webOrdreAfterNodes());
            }
        } else if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.idaOrdre)) {
            nodes = nodes.concat(this.idaOrdreNodes());

            if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.ikkePaaLager) || this.getBit(bits, this.shipmentsService.deliveryBitMasks.lagervare) || this.getBit(bits, this.shipmentsService.deliveryBitMasks.dropShipment)) {
                nodes = nodes.concat(this.idaOrdreAfterNodes());
            }
        }

        // Ikke på dropship / lager / Lagervare
        if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.dropShipment)) {
            nodes = nodes.concat([{coords: [this.column * 2 + 1, -10]}]);
            nodes = nodes.concat(this.dropShipmentNodes());
            return nodes;
        } else if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.ikkePaaLager)) {
            nodes = nodes.concat(this.ikkePaaLagerNodes());

            // Leverandør / Bestilt fjernlager
            if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.bestiltLeverandoer) || this.getBit(bits, this.shipmentsService.deliveryBitMasks.bestiltFjernlager)) {
                nodes = nodes.concat([{coords: [this.column, -19]}])
            }

            if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.bestiltLeverandoer)) {
                nodes = nodes.concat(this.bestiltLeverandoerNodes());
            } else if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.bestiltFjernlager)) {
                nodes = nodes.concat(this.bestiltFjernLagerNodes());
            } else if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.ankommet)) {
                nodes = nodes.concat([{coords: [this.column, -19]}])
                nodes = nodes.concat([{coords: [this.column + 1, -20]}]);
                nodes = nodes.concat([{coords: [this.column * 2 - 1, -20]}]);
                nodes = nodes.concat([{coords: [this.column * 2, -21]}]);
                nodes = nodes.concat([{coords: [this.column * 2, -29]}]);
                nodes = nodes.concat([{coords: [this.column * 2 - 1, -30]}]);
                nodes = nodes.concat([{coords: [this.column + 1, -30]}]);
                nodes = nodes.concat([{coords: [this.column, -31]}]);
            }

            // Ankommet
            if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.ankommet)) {
                if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.bestiltLeverandoer)) {
                    nodes = nodes.concat(this.bestiltLeverandoerAfterNodes());
                    nodes = nodes.concat([{coords: [this.column, -31]}]);
                }

                nodes = nodes.concat(this.ankommetNodes());
            } else {
                return nodes;
            }
        } else if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.lagervare)) {
            nodes = nodes.concat(this.lagervareNodes());
        } else {
            return nodes;
        }

        // Plukning startet, pakket
        if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.plukningStartet)) {
            if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.ikkePaaLager) && this.getBit(bits, this.shipmentsService.deliveryBitMasks.ankommet)) {
                nodes = nodes.concat(this.ankommetAfterNodes());
            }

            nodes = nodes.concat([{coords: [this.column * 4, -21]}]);
            nodes = nodes.concat(this.plukningStartetNodes());
        } else {
            return nodes;
        }

        if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.pakket)) {
            nodes = nodes.concat(this.pakketNodes());
        } else {
            return nodes;
        }

        // Afhentning, Chauffør, Fragtudbyder
        if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.klarTilAfhentning)) {
            nodes = nodes.concat([{coords: [this.column * 4, -44]}, {coords: [this.column * 4 - 1, -45]}]);
            nodes = nodes.concat(this.klarTilAfhentningNodes());

            if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.modtagetAfKunde) || this.getBit(bits, this.shipmentsService.deliveryBitMasks.skadet) || this.getBit(bits, this.shipmentsService.deliveryBitMasks.annulleret)) {
                nodes = nodes.concat(this.klarTilAfhentningAfterNodes());
            }
        } else if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.tildeltChauffoer)) {
            nodes = nodes.concat([{coords: [this.column * 4, -44]}, {coords: [this.column * 4 - 1, -45]}, {coords: [this.column * 3 + 1, -45]}, {coords: [this.column * 3, -46]}]);
            nodes = nodes.concat(this.tildeltChauffoerNodes());

            if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.rutePaabegyndt)) {
                nodes = nodes.concat(this.rutePaabegyndtNodes());
            }
        } else if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.hentetAfFragtUdbyder)) {
            nodes = nodes.concat(this.hentetAfFragtudbyderNodes());

            if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.modtagetAfKunde) || this.getBit(bits, this.shipmentsService.deliveryBitMasks.skadet) || this.getBit(bits, this.shipmentsService.deliveryBitMasks.annulleret)) {
                nodes = nodes.concat(this.hentetAfFragtudbyderAfterNodes());
            }
        } else if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.boxen)) {
            nodes = nodes.concat([{coords: [this.column * 4, -44]}, {coords: [this.column * 4 + 1, -45]}]);
            nodes = nodes.concat(this.boxenNodes());

            if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.boxenRumBooket)) {
                nodes = nodes.concat(this.boxenRumBooketNodes());

                if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.boxenSmsSendt)) {
                    nodes = nodes.concat(this.boxenSmsSendtNodes());

                    if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.modtagetAfKunde) || this.getBit(bits, this.shipmentsService.deliveryBitMasks.skadet) || this.getBit(bits, this.shipmentsService.deliveryBitMasks.annulleret)) {
                        nodes = nodes.concat(this.boxenSmsSendtAfterNodes());
                    }
                }
            }
        } else {
            return nodes;
        }

        // Modtaget, skadet, annulleret
        if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.modtagetAfKunde)) {
            nodes = nodes.concat(this.modtagetAfKundeNodes());
            return nodes;
        } else if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.skadet)) {
            nodes = nodes.concat(this.skadetNodes());

            if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.annulleret)) {
                nodes = nodes.concat(this.skadetAfterNodes());
                nodes = nodes.concat(this.annulleretNodes());

                return nodes;
            }
        } else if (this.getBit(bits, this.shipmentsService.deliveryBitMasks.annulleret)) {
            nodes = nodes.concat(this.annulleretBeforeNodes());
            nodes = nodes.concat(this.annulleretNodes());
            return nodes;
        }

        return nodes;
    }

    /**
     * Nodes
     */
    private webOrdreNodes() {
        return [
            {
                coords: [this.column, 1]
            },
            {
                coords: [this.column, 0],
                name: "WebOrdre",
                marker: "interchange",
                labelPos: "E"
            }
        ]
    }

    private webOrdreAfterNodes() {
        return [
            {
                coords: [this.column, -4]
            },
            {
                coords: [this.column + 1, -5]
            },
            {
                coords: [this.column * 2 - 1, -5]
            },
            {
                coords: [this.column * 2, -6]
            },
            {
                coords: [this.column * 2, -9]
            }
        ];
    }

    private ikkePaaLagerNodes() {
        return [
            {
                coords: [this.column * 2 - 1, -10]
            },
            {
                coords: [this.column + 1, -10]
            },
            {
                coords: [this.column, -11]
            },
            {
                coords: [this.column, -15],
                name: "IkkePaaLager",
                marker: "interchange",
                labelPos: "E"
            }
        ];
    }

    private bestiltFjernLagerNodes() {
        return [
            {
                coords: [this.column, -25],
                name: "BestiltFjernLager",
                marker: "interchange",
                labelPos: "E"
            }
        ];
    }

    private ankommetNodes() {
        return [
            {
                coords: [this.column, -35],
                name: "Ankommet",
                marker: "interchange",
                labelPos: "E"
            }
        ];
    }

    private ankommetAfterNodes() {
        return [
            {
                coords: [this.column, -39]
            },
            {
                coords: [this.column + 1, -40]
            },
            {
                coords: [this.column * 3 - 1, -40]
            },
            {
                coords: [this.column * 3, -39]
            },
            {
                coords: [this.column * 3, -21]
            },
            {
                coords: [this.column * 3 + 1, -20]
            },
            {
                coords: [this.column * 4 - 1, -20]
            }
        ];
    }

    private bestiltLeverandoerNodes() {
        return [
            {
                coords: [this.column - 1, -20]
            },
            {
                coords: [1, -20]
            },
            {
                coords: [0, -21]
            },
            {
                coords: [0, -25],
                name: "BestiltLeverandoer",
                marker: "interchange",
                labelPos: "E"
            }
        ];
    }

    private bestiltLeverandoerAfterNodes() {
        return [
            {
                coords: [0, -29]
            },
            {
                coords: [1, -30]
            },
            {
                coords: [this.column - 1, -30]
            }
        ];
    }

    private idaOrdreNodes() {
        return [
            {
                coords: [this.column * 3, 1]
            },
            {
                coords: [this.column * 3, 0],
                name: "IdaOrdre",
                marker: "interchange",
                labelPos: "E"
            }
        ];
    }

    private idaOrdreAfterNodes() {
        return [
            {
                coords: [this.column * 3, -4]
            },
            {
                coords: [this.column * 3 - 1, -5]
            },
            {
                coords: [this.column * 2 + 1, -5]
            },
            {
                coords: [this.column * 2, -6]
            },
            {
                coords: [this.column * 2, -9]
            }
        ];
    }

    private lagervareNodes() {
        return [
            {
                coords: [this.column * 2 + 1, -10]
            },
            {
                coords: [this.column * 4 - 1, -10]
            },
            {
                coords: [this.column * 4, -11]
            },
            {
                coords: [this.column * 4, -15],
                name: "Lagervare",
                marker: "interchange",
                labelPos: "E"
            }
        ];
    }

    private plukningStartetNodes() {
        return [
            {
                coords: [this.column * 4, -25],
                name: "PlukningStartet",
                context: this.getContext(7),
                marker: "interchange",
                labelPos: "E"
            }
        ];
    }

    private pakketNodes() {
        return [
            {
                coords: [this.column * 4, -30],
                name: "Pakket",
                marker: "interchange",
                labelPos: "E"
            }
        ];
    }

    private hentetAfFragtudbyderNodes() {
        return [
            {
                coords: [this.column * 4, -50],
                name: "HentetAfFragtudbyder",
                marker: "interchange",
                labelPos: "E"
            }
        ];
    }

    private hentetAfFragtudbyderAfterNodes() {
        return [
            {
                coords: [this.column * 4, -64],
            },
            {
                coords: [this.column * 4 - 1, -65],
            },
            {
                coords: [this.column * 3 + 1, -65],
            },
            {
                coords: [this.column * 3, -66],
            }
        ];
    }

    private annulleretBeforeNodes() {
        return [
            {
                coords: [this.column * 3, -69],
            },
            {
                coords: [this.column * 3 + 1, -70],
            },
            {
                coords: [this.column * 4 - 1, -70],
            },
            {
                coords: [this.column * 4, -71],
            }
        ]
    }

    private annulleretNodes() {
        return [
            {
                coords: [this.column * 4, -75],
                name: "Annulleret",
                marker: "interchange",
                labelPos: "E"
            }
        ]
    }

    private dropShipmentNodes() {
        return [
            {
                coords: [this.column * 5 - 1, -10]
            },
            {
                coords: [this.column * 5, -11]
            },
            {
                coords: [this.column * 5, -15],
                name: "DropShipment",
                marker: "interchange",
                labelPos: "E"
            }
        ];
    }

    private klarTilAfhentningNodes() {
        return [
            {
                coords: [this.column * 2 + 1, -45]
            },
            {
                coords: [this.column * 2, -46]
            },
            {
                coords: [this.column * 2, -50],
                name: "KlarTilAfhentning",
                marker: "interchange",
                labelPos: "E"
            }
        ];
    }

    private klarTilAfhentningAfterNodes() {
        return [
            {
                coords: [this.column * 2, -64]
            },
            {
                coords: [this.column * 2 + 1, -65]
            },
            {
                coords: [this.column * 3 - 1, -65]
            },
            {
                coords: [this.column * 3, -66]
            }
        ];
    }

    private modtagetAfKundeNodes() {
        return [
            {
                coords: [this.column * 3, -69]
            },
            {
                coords: [this.column * 3 - 1, -70]
            },
            {
                coords: [this.column * 2 + 1, -70]
            },
            {
                coords: [this.column * 2, -71]
            },
            {
                coords: [this.column * 2, -75],
                name: "ModtagetAfKunde",
                marker: "interchange",
                labelPos: "E"
            }
        ];
    }

    private tildeltChauffoerNodes() {
        return [
            {
                coords: [this.column * 3, -50],
                name: "TildeltChauffoer",
                marker: "interchange",
                labelPos: "E"
            }
        ];
    }

    private rutePaabegyndtNodes() {
        return [
            {
                coords: [this.column * 3, -55],
                name: "RutePaabegyndt",
                marker: "interchange",
                labelPos: "E"
            }
        ];
    }

    private skadetNodes() {
        return [
            {
                coords: [this.column * 3, -75],
                name: "Skadet",
                marker: "interchange",
                labelPos: "E"
            }
        ];
    }

    private skadetAfterNodes() {
        return [
            {
                coords: [this.column * 3, -79]
            },
            {
                coords: [this.column * 3 + 1, -80]
            },
            {
                coords: [this.column * 4 - 1, -80]
            },
            {
                coords: [this.column * 4, -79]
            }
        ];
    }

    private boxenNodes() {
        return [
            {
                coords: [this.column * 5 - 1, -45]
            },
            {
                coords: [this.column * 5, -46]
            },
            {
                coords: [this.column * 5, -50],
                name: "Boxen",
                marker: "interchange",
                labelPos: "E"
            }
        ];
    }

    private boxenRumBooketNodes() {
        return [
            {
                coords: [this.column * 5, -55],
                name: "BoxenRumBooket",
                marker: "interchange",
                labelPos: "E"
            }
        ];
    }

    private boxenSmsSendtNodes() {
        return [
            {
                coords: [this.column * 5, -60],
                name: "BoxenSmsSendt",
                marker: "interchange",
                labelPos: "E"
            }
        ];
    }

    private boxenSmsSendtAfterNodes() {
        return [
            {
                coords: [this.column * 5, -64]
            },
            {
                coords: [this.column * 5 - 1, -65]
            },
            {
                coords: [this.column * 3 + 1, -65]
            },
            {
                coords: [this.column * 3, -66]
            }
        ];
    }

    /**
     * Get value for bit a given position
     *
     * @param number
     * @param position
     */
    getBit(number: number, position: number) {
        return (number & (1 << position)) !== 0;
    }

    getLabel(position: number, initial: string): string {
        if (this.shipment.delivery_labels.hasOwnProperty(position)) {
            return this.shipment.delivery_labels[position];
        }

        return initial;
    }

    /**
     * Get context for given position
     *
     * @param position
     * @private
     */
    getContext(position: number): string {
        if (this.shipment.delivery_context.hasOwnProperty(position)) {
            return this.shipment.delivery_context[position];
        }

        return '';
    }

    /**
     * DEVELOPMENT
     *
     * Toggles progress bit at given position
     *
     * @param position
     */
    toggleBit(position: number) {
        const bitValueNormalized = !this.getBit(this.shipment.delivery_progress, position) ? 1 : 0;
        const clearMask = ~(1 << position);
        this.shipment.delivery_progress = (this.shipment.delivery_progress & clearMask) | (bitValueNormalized << position);

        this.error = undefined;

        this.draw();
    }

    /**
     * DEVELOPMENT
     *
     * Runs an interval that picks a random set of bits for progress line to brute force any errors
     */
    public test() {
        if (this.testing) {
            clearInterval(this.testing);

            this.testing = undefined;
            this.iteration = 0;
            this.rejections = 0;
            this.error = undefined;

            return;
        }

        this.iteration = 0;
        this.rejections = 0;
        this.error = undefined;

        this.testing = setInterval(() => {
            this.shipment.delivery_progress = Math.floor(Math.random() * (131071 + 1));
            this.shipment.estimated_delivery_progress = Math.floor(Math.random() * (131071 - this.shipment.delivery_progress + 1) + this.shipment.delivery_progress);

            if (this.isBitsRejected(this.shipment.delivery_progress) || this.isBitsRejected(this.shipment.estimated_delivery_progress)) {
                this.rejections++;
                return;
            }

            this.iteration++;

            try {
                this.draw();
            } catch (error) {
                clearInterval(this.testing);

                this.error = error.message;
            }
        }, 100);
    }

    /**
     * DEVELOPMENT
     *
     * Method for rejecting randomly generated sets of bits to save time
     */
    public isBitsRejected(bits: number) {
        return !this.getBit(bits, this.shipmentsService.deliveryBitMasks.webOrdre) && !this.getBit(bits, this.shipmentsService.deliveryBitMasks.idaOrdre);
    }
}
